import type { PriceId, QualifyingStatusEnum } from '@iheartjane/types';

// eslint-disable-next-line no-restricted-imports
import type { BrandSpecial as tBrandSpecial } from '@jane/shared/models';
import type { BrandSpecial } from '@jane/shared/types';

/**
 * Gets the applicable price IDs for a brand special based on its type and qualifying status.
 * @param brandSpecial - The brand special to get price IDs for
 * @param qualifyingStatus - The current qualifying status
 * @returns Array of applicable price ID strings
 *
 * Logic:
 * - Product type: Returns the applicable_weights array directly
 * - Bundle types (buy_x_and_save, buy_x_get_y):
 *   - If qualified or applied: Returns discount price IDs
 *   - Otherwise: Returns qualify price IDs
 */
export function getApplicablePriceIds(
  brandSpecial: BrandSpecial | tBrandSpecial,
  qualifyingStatus: QualifyingStatusEnum
): PriceId[] {
  if (!brandSpecial) return [];

  const { special_type, applicable_weights } = brandSpecial;

  // Handle null or undefined applicable_weights
  if (!applicable_weights) {
    return [];
  }

  // Product type: return applicable_weights array directly
  if (special_type === 'product') {
    // For product type, applicable_weights should be an array
    if (Array.isArray(applicable_weights)) {
      return applicable_weights as PriceId[];
    }
    return [];
  }

  // Bundle types: buy_x_and_save, buy_x_get_y
  if (special_type === 'buy_x_and_save' || special_type === 'buy_x_get_y') {
    // For bundle types, applicable_weights should be an object with qualify/discount arrays
    if (
      typeof applicable_weights === 'object' &&
      !Array.isArray(applicable_weights)
    ) {
      const isQualifiedOrApplied =
        qualifyingStatus === 'qualified' || qualifyingStatus === 'applied';

      if (isQualifiedOrApplied) {
        return (applicable_weights.discount as PriceId[]) || [];
      } else {
        return (applicable_weights.qualify as PriceId[]) || [];
      }
    }
    return [];
  }

  // Fallback for unknown special types
  return [];
}
